import { useLocation, Link } from "@/lib/router-compat";
import { useEffect } from "react";
import { Home, Search, Video, Podcast, Globe, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import UnifiedSearch from "@/components/search/UnifiedSearch";
import { useT } from "@/i18n/useLocale";
import { T } from "@/i18n/T";

const NotFound = () => {
  const location = useLocation();
  const t = useT();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  const popularLinks = [
    { to: "/", icon: Home, label: t("nav.news") },
    { to: "/social", icon: Video, label: t("nav.social") },
    { to: "/markets", icon: Globe, label: t("nav.markets") },
    { to: "/podcasts", icon: Podcast, label: t("nav.podcasts") },
    { to: "/learn", icon: GraduationCap, label: t("nav.learn") },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="max-w-2xl w-full text-center space-y-8">
        {/* 404 Header */}
        <div className="space-y-4">
          <h1 className="text-9xl font-bold text-primary">404</h1>
          <h2 className="text-3xl font-bold text-foreground">{t("notFound.title")}</h2>
          <p className="text-lg text-muted-foreground">
            {t("notFound.body")}
          </p>
        </div>

        {/* Search Section */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-foreground mb-2">
            <Search className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold"><T k="auto.search-for-what-you-need-7d5b" /></h3>
          </div>
          <UnifiedSearch 
            placeholder={t("feed.searchPlaceholder")}
            mode="global"
            showHistory={false}
            className="w-full"
          />
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" className="gap-2">
            <Link to="/">
              <Home className="w-5 h-5" />
              {t("common.backHome")}
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="gap-2">
            <Link to="/search">
              <Search className="w-5 h-5" />
              <T k="auto.advanced-search-cd4e" />
            </Link>
          </Button>
        </div>

        {/* Popular Links */}
        <div className="space-y-4 pt-4">
          <h3 className="text-sm font-medium text-muted-foreground">
            {t("notFound.popular")}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {popularLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border bg-card hover:bg-accent hover:border-primary transition-all group"
                >
                  <Icon className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors" />
                  <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                    {link.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Error Path Info */}
        <p className="text-xs text-muted-foreground pt-4">
          Attempted path: <code className="bg-muted px-2 py-1 rounded">{location.pathname}</code>
        </p>
      </div>
    </div>
  );
};

export default NotFound;
